import { FormIdUtil, BuildTags } from '@onshape/www-inertia';
import type { NuxtApp } from 'nuxt/app';
import { useFormState } from './useFormState';

const isFormFortAdded = ref(false);
export const wwwHekateFormDefinitions = {} as WwwHekateFormDefinitions;

// the time the module was generated
const now = Date.now();

export const useFormFiles = ({
  translationsStaticAsset,
  refreshOldTabsStaticAsset,
}: {
  translationsStaticAsset: StaticAssetInfo | null;
  refreshOldTabsStaticAsset: StaticAssetInfo | null;
}) => {
  if (!translationsStaticAsset) {
    throw new Error('Translations static asset is required');
  }
  if (!refreshOldTabsStaticAsset) {
    throw new Error('Refresh old tabs static asset is required');
  }

  const loadFormDefinition = async ({ formId, nuxtApp }: { formId: string; nuxtApp: NuxtApp }) => {
    const { data, error } = await useAsyncData(`form-definition-${formId}`, async () => {
      const url = new URL('https://www.onshape.com/api/fb/getFormDefinitionJson');
      url.searchParams.set('formId', formId);
      url.searchParams.set('now', now.toString());
      url.searchParams.set('pv', BuildTags.packageVersion);
      url.searchParams.set('bh', BuildTags.buildHash);
      const resp = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-ssg-host': 'www.onshape.com',
        },
      });
      const json = await resp.json();
      return json;
    });

    if (!data?.value || error.value) {
      throw new Error(`Form definition not found for formId: ${formId}, error: ${error.value}`);
    }

    if (formId === FormIdUtil.names.unifiedSignup) {
      await nuxtApp.runWithContext(
        async () => await loadFormDefinition({ formId: FormIdUtil.names.signupSurvey, nuxtApp })
      );
    }

    wwwHekateFormDefinitions[formId] = data.value;
    return data.value;
  };

  /** Add form definitions, translations and formfort.js to the page if needed */
  const loadHekate = async ({ formId, nuxtApp }: { formId: string; nuxtApp: NuxtApp }) => {
    nuxtApp.runWithContext(() => {
      const { isUnifiedSignupForm } = useFormState();
      isUnifiedSignupForm.value =
        isUnifiedSignupForm.value || FormIdUtil.isCadInviteForm({ formId });

      if (isFormFortAdded.value) return;

      useHead({
        script: [
          {
            src: refreshOldTabsStaticAsset.cdnPathname,
            type: 'text/javascript',
            async: true,
            tagPriority: 4,
            'data-currentPathname': refreshOldTabsStaticAsset.currentPathname,
          },
          {
            src: translationsStaticAsset.cdnPathname,
            async: false, // this must be loaded before the formfort.js
            tagPriority: 2,
            'data-currentPathname': translationsStaticAsset.currentPathname,
          },
        ],
      });
      isFormFortAdded.value = true;
    });
  };

  return {
    loadHekate,
    loadFormDefinition,
  };
};
